import React, { useState, useMemo } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  Download, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  EyeOff,
  Settings
} from 'lucide-react';
import { downloadWorkbook } from '../utils/downloadWorkbook';
import { useWorkspaceAccess } from '../hooks/useWorkspaceAccess';
import ActionMenu from './ActionMenu';
import './DataTable.scss';

const formatTableHeader = (label = '') =>
  String(label)
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word) => {
      if (word.length <= 3 && word === word.toUpperCase()) {
        return word
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')

const DataTable = ({
  data = [],
  columns = [],
  title = "Data Table",
  searchable = true,
  sortable = true,
  exportable = true,
  downloadPermission = '',
  pagination = true,
  pageSize = 10,
  showActions = true,
  showCheckboxes = true,
  onRowClick = null,
  onEdit = null,
  onDelete = null,
  onView = null,
  onCopy = null,
  onRiskAnalysis = null,
  customActions = [],
  rowActions = null,
  onRequestDelete = null,
  className = "",
  searchPlaceholder = "Search...",
  emptyMessage = "No data available",
  loading = false,
  initialHiddenColumns = [],
  rowIdKey = 'id'
}) => {
  const getRowId = (row) => {
    if (!row) return undefined;
    if (row.id !== undefined && row.id !== null) return row.id;
    if (rowIdKey && row[rowIdKey] !== undefined) return row[rowIdKey];
    return row._id;
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  const [hiddenColumns, setHiddenColumns] = useState(initialHiddenColumns);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, right: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const access = useWorkspaceAccess();
  const canDownload = !downloadPermission || access.allowed([downloadPermission]);
  const [filters, setFilters] = useState({});

  // Close column settings when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnSettings && !event.target.closest('.position-relative')) {
        setShowColumnSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnSettings]);

  // Tooltip component
  const Tooltip = ({ show, text, x, y }) => {
    if (!show) return null;
    
    return (
      <div
        style={{
          position: 'fixed',
          left: x,
          top: y - 35,
          backgroundColor: 'var(--text-primary)',
          color: 'var(--white)',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          zIndex: 1000,
          pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
        }}
      >
        {text}
      </div>
    );
  };

  // Handle tooltip
  const handleTooltip = (show, text, event) => {
    if (show && event) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltip({
        show: true,
        text,
        x: rect.left + rect.width / 2,
        y: rect.top
      });
    } else {
      setTooltip({ show: false, text: '', x: 0, y: 0 });
    }
  };

  // Handle column visibility
  const toggleColumnVisibility = (columnKey) => {
    setHiddenColumns(prev => 
      prev.includes(columnKey) 
        ? prev.filter(key => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  // Get visible columns
  const visibleColumns = useMemo(() => {
    return columns.filter(column => !hiddenColumns.includes(column.key));
  }, [columns, hiddenColumns]);

  const filterFields = useMemo(() => {
    return visibleColumns
      .filter((column) => column.key !== 'actions' && column.filterable !== false)
      .map((column) => {
        const values = [...new Set(
          (data || [])
            .map((row) => row?.[column.key])
            .filter((value) => value !== null && value !== undefined && value !== '' && typeof value !== 'object')
            .map((value) => String(value))
        )];
        const canChoose = values.length >= 2
          && values.length <= 12
          && values.every((value) => value.length <= 40);
        return canChoose ? { key: column.key, label: column.label, options: values } : null;
      })
      .filter(Boolean);
  }, [visibleColumns, data]);

  const activeFilterCount = Object.values(filters).filter((value) => value && value !== '').length;

  // Filter data based on search term and filters
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    
    let filtered = data;
    
    // Apply search term
    if (searchTerm) {
      filtered = filtered.filter(row => {
        if (!row) return false;
        return visibleColumns.some(column => {
          const value = row[column.key];
          if (value === null || value === undefined) return false;
          return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
        });
      });
    }
    
    // Apply filters
    const choiceKeys = new Set(filterFields.map((field) => field.key));
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        filtered = filtered.filter(row => {
          if (!row) return false;
          const rowValue = row[key];
          if (rowValue === null || rowValue === undefined) return false;
          if (choiceKeys.has(key)) {
            return String(rowValue).toLowerCase() === String(value).toLowerCase();
          }
          if (typeof rowValue === 'string') {
            return rowValue.toLowerCase().includes(value.toLowerCase());
          }
          return rowValue === value;
        });
      }
    });
    
    return filtered;
  }, [data, searchTerm, visibleColumns, filters, filterFields]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, pageSize, pagination]);

  // Calculate total pages
  const totalPages = Math.ceil(sortedData.length / pageSize);

  // Handle sort
  const handleSort = (key) => {
    if (!sortable) return;
    
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle row selection
  const handleRowSelect = (rowId, isSelected) => {
    if (isSelected) {
      setSelectedRows(prev => [...prev, rowId]);
    } else {
      setSelectedRows(prev => prev.filter(id => id !== rowId));
    }
  };

  // Handle select all
  const selectionPool = onRequestDelete ? sortedData : paginatedData;

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedRows(selectionPool.map((row) => getRowId(row)));
    } else {
      setSelectedRows([]);
    }
  };

  // Reset selected rows when checkboxes are disabled
  React.useEffect(() => {
    if (!showCheckboxes) {
      setSelectedRows([]);
    }
  }, [showCheckboxes]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  // Export to Excel
  const handleExport = () => {
    const exportData = selectedRows.length > 0 
      ? data.filter((row) => selectedRows.includes(getRowId(row)))
      : sortedData;

    downloadWorkbook(
      `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`,
      exportData.map((row) => {
        const exportRow = {};
        columns.forEach((column) => {
          const value = row[column.key];
          exportRow[column.label] = value == null || typeof value === 'object' ? '' : value;
        });
        return exportRow;
      })
    );
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (!sortable) return null;
    
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? 
        <ArrowUp size={16} className="text-primary" /> : 
        <ArrowDown size={16} className="text-primary" />;
    }
    return <ArrowUpDown size={16} className="text-muted" />;
  };

  // Render cell content
  const renderCell = (row, column) => {
    if (!row || !column) return null;
    
    const value = row[column.key];
    
    // If column has a render function, use it (even for objects)
    if (column.render) {
      try {
        return column.render(value, row);
      } catch (error) {
        console.error('Error in column render function:', error);
        return String(value || '');
      }
    }
    
    // Safety check: don't render objects directly if no render function
    if (typeof value === 'object' && value !== null) {
      console.warn('Attempted to render object in DataTable without render function:', value);
      return JSON.stringify(value);
    }
    
    if (column.type === 'badge') {
      const badgeClass = column.badgeClass ? column.badgeClass(value) : 'badge-secondary';
      return <span className={`badge ${badgeClass}`}>{String(value || '')}</span>;
    }
    
    if (column.type === 'date') {
      return new Date(value).toLocaleDateString();
    }
    
    if (column.type === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    }
    
    return String(value || '');
  };

  const buildRowActions = (row) => {
    if (typeof rowActions === 'function') return rowActions(row) || [];
    return [
      ...(onView ? [{ type: 'view', label: 'View' }] : []),
      ...(onEdit ? [{ type: 'edit', label: 'Edit' }] : []),
      ...(onCopy ? [{ type: 'copy', label: 'Copy' }] : []),
      ...(onRiskAnalysis ? [{ type: 'risk', label: 'AI Risk Analysis' }] : []),
      ...customActions,
      ...(onDelete ? [{ type: 'delete', label: 'Delete', variant: 'danger' }] : [])
    ];
  };

  const handleRowAction = (action, row) => {
    switch (action.type) {
      case 'view':
        onView && onView(row);
        break;
      case 'edit':
        onEdit && onEdit(row);
        break;
      case 'copy':
        onCopy && onCopy(row);
        break;
      case 'risk':
        onRiskAnalysis && onRiskAnalysis(row);
        break;
      case 'delete':
        onDelete && onDelete(row);
        break;
      default:
        if (action.onClick) {
          action.onClick(row);
        }
        break;
    }
  };

  const mobileTitleColumn = useMemo(() => {
    const preferred = visibleColumns.find((column) => column.mobileTitle || column.primary)
    return preferred || visibleColumns[0] || null
  }, [visibleColumns])

  const mobileFieldColumns = useMemo(() => {
    if (!mobileTitleColumn) return visibleColumns
    return visibleColumns.filter((column) => column.key !== mobileTitleColumn.key)
  }, [mobileTitleColumn, visibleColumns])

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Tooltip show={tooltip.show} text={tooltip.text} x={tooltip.x} y={tooltip.y} />
      <div className={`card datatable-card ${className}`}>
        {/* Header */}
        <div className="card-header datatable-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0 datatable-title">{title}</h5>
            <div className="d-flex gap-2">
              {onRequestDelete ? (
                <>
                  <button
                    type="button"
                    className="btn btn-sm datatable-btn datatable-btn--danger"
                    disabled={!selectedRows.length}
                    onClick={() => onRequestDelete({
                      mode: 'selected',
                      ids: selectedRows,
                      rows: data.filter((row) => selectedRows.includes(getRowId(row)))
                    })}
                  >
                    Delete selected
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm datatable-btn datatable-btn--danger"
                    disabled={!sortedData.length}
                    onClick={() => onRequestDelete({
                      mode: 'all',
                      ids: sortedData.map((row) => getRowId(row)),
                      rows: sortedData
                    })}
                  >
                    Delete all
                  </button>
                </>
              ) : null}
              {exportable && canDownload && (
                <button 
                  className="btn btn-sm datatable-btn"
                  onClick={handleExport}
                  disabled={data.length === 0}
                >
                  <Download size={16} className="me-1" />
                  Export Excel
                </button>
              )}
              <button 
                className={`btn btn-sm datatable-btn${showFilters || activeFilterCount ? ' datatable-btn--open' : ''}`}
                aria-expanded={showFilters}
                onClick={() => setShowFilters((open) => !open)}
              >
                <Filter size={16} className="me-1" />
                Filter{activeFilterCount ? ` · ${activeFilterCount}` : ''}
              </button>
              <div className="position-relative" style={{ zIndex: 10000 }}>
                <button 
                  className={`btn btn-sm datatable-btn${showColumnSettings ? ' datatable-btn--open' : ''}`}
                  aria-expanded={showColumnSettings}
                  aria-haspopup="dialog"
                  onClick={() => setShowColumnSettings(!showColumnSettings)}
                  onMouseEnter={(e) => {
                    handleTooltip(true, 'Column Settings', e);
                  }}
                  onMouseLeave={(e) => {
                    handleTooltip(false);
                  }}
                >
                  <Settings size={16} />
                </button>
                
                {/* Column Settings Dropdown */}
                {showColumnSettings && (
                  <div className="datatable-column-settings" role="dialog" aria-label="Columns">
                    <div className="datatable-column-settings__head">
                      <span>Columns</span>
                      <span className="datatable-column-settings__count">
                        {visibleColumns.length} of {columns.length}
                      </span>
                    </div>
                    <div className="datatable-column-settings__list">
                      {columns.map((column) => {
                        const shown = !hiddenColumns.includes(column.key);
                        return (
                          <label
                            key={column.key}
                            className={`datatable-column-settings__row${shown ? ' is-on' : ''}`}
                            htmlFor={`column-${column.key}`}
                          >
                            <input
                              id={`column-${column.key}`}
                              type="checkbox"
                              checked={shown}
                              onChange={() => toggleColumnVisibility(column.key)}
                            />
                            <span>{formatTableHeader(column.label)}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {showFilters && (
            <div className="datatable-filterbar" role="region" aria-label="Filters">
              {filterFields.length ? (
                <>
                  <div className="datatable-filterbar__head">
                    <span className="datatable-filterbar__label">
                      {activeFilterCount
                        ? `${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'} applied`
                        : 'Choose a value to narrow the list'}
                    </span>
                    <button
                      type="button"
                      className="datatable-filterbar__clear"
                      onClick={() => setFilters({})}
                      disabled={!activeFilterCount}
                    >
                      Clear
                    </button>
                  </div>
                  <div className="datatable-filterbar__grid">
                    {filterFields.map((field) => {
                      const selected = Boolean(filters[field.key]);
                      return (
                        <label
                          key={field.key}
                          className={`datatable-filterbar__field${selected ? ' is-set' : ''}`}
                        >
                          <span>{formatTableHeader(field.label)}</span>
                          <select
                            value={filters[field.key] || ''}
                            onChange={(event) => setFilters((prev) => ({ ...prev, [field.key]: event.target.value }))}
                          >
                            <option value="">All</option>
                            {field.options.map((option) => (
                              <option key={option} value={option}>{formatTableHeader(option)}</option>
                            ))}
                          </select>
                        </label>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="datatable-filterbar__hint">Use the search box to narrow this list.</p>
              )}
            </div>
          )}
        </div>

        {/* Search Bar */}
        {searchable && (
          <div className="card-body datatable-search-section">
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div className="datatable-search-container">
                <span className="input-group-text datatable-search-icon">
                  <Search size={16} />
                </span>
                <input
                  type="text"
                  className="form-control datatable-search-input"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <span className="datatable-info-text">
                Showing {paginatedData.length} of {sortedData.length} entries
                {selectedRows.length > 0 && ` (${selectedRows.length} selected)`}
              </span>
            </div>
          </div>
        )}

        {/* Desktop table */}
        <div className="table-responsive datatable-desktop">
          <table className="table table-hover mb-0 datatable-table">
            <thead className="table-light datatable-thead">
              <tr>
                {showCheckboxes && (
                  <th className="datatable-checkbox-column">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedRows.length === selectionPool.length && selectionPool.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                )}
                {visibleColumns.map((column) => (
                  <th 
                    key={column.key}
                    className={`datatable-th ${sortable ? 'sortable' : ''}`}
                    style={{ width: column.width || 'auto' }}
                    onClick={() => handleSort(column.key)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      {formatTableHeader(column.label)}
                      {getSortIcon(column.key)}
                    </div>
                  </th>
                ))}
                {showActions && (
                  <th className="datatable-actions-column">{formatTableHeader('Actions')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td 
                    colSpan={visibleColumns.length + (showCheckboxes ? 1 : 0) + (showActions ? 1 : 0)} 
                    className="text-center py-4 text-muted datatable-empty"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, index) => {
                  const rowId = getRowId(row);
                  return (
                  <tr 
                    key={rowId || index}
                    className={`datatable-row ${selectedRows.includes(rowId) ? 'table-active' : ''} ${onRowClick ? 'clickable' : ''}`}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {showCheckboxes && (
                      <td className="datatable-checkbox-cell">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedRows.includes(rowId)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleRowSelect(rowId, e.target.checked);
                          }}
                        />
                      </td>
                    )}
                    {visibleColumns.map((column) => (
                      <td key={column.key} className="datatable-cell">
                        {renderCell(row, column)}
                      </td>
                    ))}
                    {showActions && (
                      <td className="datatable-actions-cell">
                        <div className="d-flex justify-content-center">
                          <ActionMenu
                            actions={buildRowActions(row)}
                            onAction={(action) => handleRowAction(action, row)}
                            size="sm"
                            variant="outline"
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="datatable-mobile-list">
          {paginatedData.length === 0 ? (
            <div className="datatable-mobile-empty">{emptyMessage}</div>
          ) : (
            paginatedData.map((row, index) => {
              const rowId = getRowId(row);
              return (
              <article
                key={rowId || index}
                className={`datatable-mobile-card ${selectedRows.includes(rowId) ? 'is-selected' : ''} ${onRowClick ? 'is-clickable' : ''}`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {showCheckboxes ? (
                  <div className="datatable-mobile-card__select">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedRows.includes(rowId)}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleRowSelect(rowId, e.target.checked)
                      }}
                    />
                  </div>
                ) : null}

                <div className="datatable-mobile-card__header">
                  <div className="datatable-mobile-card__title">
                    {mobileTitleColumn ? renderCell(row, mobileTitleColumn) : null}
                  </div>
                  {showActions ? (
                    <div
                      className="datatable-mobile-card__actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ActionMenu
                        actions={buildRowActions(row)}
                        onAction={(action) => handleRowAction(action, row)}
                        size="sm"
                        variant="outline"
                      />
                    </div>
                  ) : null}
                </div>

                <dl className="datatable-mobile-card__fields">
                  {mobileFieldColumns.map((column) => (
                    <div key={column.key} className="datatable-mobile-card__field">
                      <dt>{formatTableHeader(column.label)}</dt>
                      <dd>{renderCell(row, column)}</dd>
                    </div>
                  ))}
                </dl>
              </article>
            );
            })
          )}
        </div>

        {/* Pagination */}
        {pagination && totalPages > 1 && (
          <div className="card-footer datatable-footer">
            <div className="d-flex justify-content-between align-items-center">
              <div className="datatable-pagination-info">
                Page {currentPage} of {totalPages} ({sortedData.length} total entries)
              </div>
              <nav>
                <ul className="pagination pagination-sm mb-0 datatable-pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link datatable-page-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} />
                    </button>
                  </li>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                        <button 
                          className="page-link datatable-page-btn"
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link datatable-page-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DataTable;
